/**
 * Converts a date value into a valid Date object.
 *
 * Supports:
 * - Date objects
 * - Unix timestamps in seconds, for example 1963045717
 * - DD/MM/YYYY, for example "10/04/2026"
 * - DD/MM/YYYY HH:mm, for example "10/04/2026 16:44"
 * - DD/MM/YYYY HH:mm:ss, for example "10/04/2026 16:44:30"
 * - Date strings supported by the JavaScript Date constructor
 *
 * @param {string|number|Date} dateValue - Date value to parse.
 * @returns {Date|null} A valid Date object, or null if invalid.
 */
const parseDateValue = (dateValue) => {
    if (!dateValue) return null
    if (dateValue instanceof Date) return dateValue

    if (isUnixTimestamp(dateValue)) {
        const date = new Date(Number(dateValue) * 1000)
        return isNaN(date.getTime()) ? null : date
    }

    const timeDate = getDateFromTimeValue(dateValue)
    if (timeDate) return timeDate

    const dayMonthYearDate = getDateFromDayMonthYearValue(dateValue)
    if (dayMonthYearDate) return dayMonthYearDate

    const date = new Date(dateValue)
    return isNaN(date.getTime()) ? null : date
}

/**
 * Checks if a given value is a Unix timestamp (positive integer in seconds).
 *
 * @param {string|number} dateValue - The value to check.
 * @returns {boolean} True if the value is a Unix timestamp, false otherwise.
 */
const isUnixTimestamp = (dateValue) => {
    return /^\d+$/.test(String(dateValue))
}

/**
 * Converts a date value into a valid Date object.
 *
 * Supports:
 * - Date strings, for example "2026-04-10 16:44:30"
 * - Unix timestamps in seconds, for example 1963045717
 *
 * @param {string|number} dateValue - Date string or Unix timestamp in seconds.
 * @returns {Date|null} A valid Date object, or null if the input is invalid.
 */
const getDateFromValue = (dateValue) => {
    if (!dateValue) return null

    const date = isUnixTimestamp(dateValue)
        ? new Date(Number(dateValue) * 1000)
        : new Date(dateValue)

    if (isNaN(date.getTime())) return null

    return date
}

/**
 * Formats a date value into DD/MM/YYYY format.
 *
 * Supports:
 * - Date strings, for example "2026-04-10 16:44:30"
 * - Unix timestamps in seconds, for example 1963045717
 *
 * @param {string|number} dateValue - Date string or Unix timestamp in seconds.
 * @returns {string} Formatted date in DD/MM/YYYY format, or an empty string if invalid.
 */
const formatDate = (dateValue) => {
    const date = getDateFromValue(dateValue)
    if (!date) return ''

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}/${month}/${year}`
}

/**
 * Checks if a string is in DD/MM/YYYY format.
 *
 * @param {string|number} dateValue - The value to check.
 * @returns {boolean} True if the value matches DD/MM/YYYY format, false otherwise.
 */
const isDayMonthYearFormat = (dateValue) => {
    return /^\d{2}\/\d{2}\/\d{4}$/.test(String(dateValue))
}

/**
 * Converts DD/MM/YYYY format to YYYY-MM-DD format.
 *
 * @param {string} dateValue - Date string in DD/MM/YYYY format.
 * @returns {string} Date string in YYYY-MM-DD format.
 */
const convertDMYToISO = (dateValue) => {
    const [day, month, year] = dateValue.split('/')
    return `${year}-${month}-${day}`
}
/**
 * Checks if a string is a valid datetime.
 *
 * Supports:
 * - DD/MM/YYYY HH:mm, for example "10/04/2026 16:44"
 * - DD/MM/YYYY HH:mm:ss, for example "10/04/2026 16:44:30"
 * - YYYY-MM-DDTHH:mm:ss, for example "2026-04-10T16:44:30"
 *
 * @param {string|number} dateValue - The value to check.
 * @returns {boolean} True if the value is a valid datetime string, false otherwise.
 */
const isDateTime = (dateValue) => {
    if (!dateValue) return false

    const value = String(dateValue)

    const isDateTimeFormat =
        /^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}(?::\d{2})?$/.test(value) ||
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)

    return isDateTimeFormat && !!parseDateValue(value)
}

/**
 * Converts DD/MM/YYYY or DD/MM/YYYY HH:mm format to a valid Date object.
 *
 * Supports:
 * - DD/MM/YYYY, for example "10/04/2026"
 * - DD/MM/YYYY HH:mm, for example "10/04/2026 16:44"
 * - DD/MM/YYYY HH:mm:ss, for example "10/04/2026 16:44:30"
 *
 * @param {string|number} dateValue - Date string to parse.
 * @returns {Date|null} A valid Date object, or null if the input is invalid.
 */
const getDateFromDayMonthYearValue = (dateValue) => {
    const match = String(dateValue).match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    )

    if (!match) return null

    const [, day, month, year, hours = '00', minutes = '00', seconds = '00'] =
        match

    const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds)
    )

    if (
        date.getFullYear() !== Number(year) ||
        date.getMonth() !== Number(month) - 1 ||
        date.getDate() !== Number(day) ||
        date.getHours() !== Number(hours) ||
        date.getMinutes() !== Number(minutes) ||
        date.getSeconds() !== Number(seconds)
    ) {
        return null
    }

    return date
}

/**
 * Formats a date value into YYYY-MM-DD format.
 *
 * Supports:
 * - Date strings, for example "2026-04-10 16:44:30"
 * - Unix timestamps in seconds, for example 1963045717
 * - DD/MM/YYYY format, for example "10/04/2026"
 *
 * @param {string|number} dateValue - Date string or Unix timestamp in seconds.
 * @returns {string} Formatted date in YYYY-MM-DD format, or an empty string if invalid.
 */
const formatDateISO = (dateValue) => {
    // Check if dateValue is DD/MM/YYYY format and convert it
    if (isDayMonthYearFormat(dateValue)) {
        return convertDMYToISO(dateValue)
    }

    const date = getDateFromValue(dateValue)
    if (!date) return ''

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${year}-${month}-${day}`
}

/**
 * Extracts and formats the time component from a date value in 24-hour HH:mm format.
 *
 * Supports:
 * - Date strings, for example "2026-04-10 16:44:30"
 * - Unix timestamps in seconds, for example 1963045717
 *
 * @param {string|number} dateValue - Date string or Unix timestamp in seconds.
 * @param {boolean} hour12 - Whether to use 12-hour format with AM/PM. Defaults to false.
 * @returns {string} Formatted time in HH:mm format, or an empty string if invalid.
 */
const getTimeFromDate = (dateValue, hour12 = false) => {
    const date = getDateFromValue(dateValue)
    if (!date) return ''

    return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: hour12,
    }).format(date)
}

/**
 * Formats a date value into DD/MM/YYYY HH:mm AM/PM format.
 *
 * Supports:
 * - Date strings, for example "2026-04-10 16:44:30"
 * - Unix timestamps in seconds, for example 1963045717
 *
 * @param {string|number} dateValue - Date string or Unix timestamp in seconds.
 * @param hour12
 * @returns {string} Formatted date and time in DD/MM/YYYY HH:mm AM/PM format, or an empty string if invalid.
 */
const formatDateAndTime = (dateValue, hour12 = false) => {
    return `${formatDate(dateValue)} ${getTimeFromDate(dateValue, hour12)}`
}

/**
 * Validates if a string is a valid ISO date string (containing 'T' separator).
 * @param {string} dateString - The string to validate
 * @returns {boolean} True if the string is a valid ISO date string, false otherwise
 */
const isDate = (dateString) => {
    if (!dateString) return false
    const date = new Date(dateString)
    return !isNaN(date.getTime()) && dateString.includes('T')
}

/**
 * Converts a date value into a human-readable relative time string (e.g., "2 days ago").
 *
 * Supports:
 * - Date strings, for example "2026-04-10 16:44:30"
 * - Unix timestamps in seconds, for example 1963045717
 *
 * @param {string|number} value - Date string or Unix timestamp in seconds.
 * @returns {string} A human-readable relative time string, or an empty string if invalid.
 */
const getDateFromNow = (value) => {
    // Handle Date object directly
    const date = getDateFromValue(value)

    if (!date) return ''

    const now = new Date()
    const seconds = Math.floor((now - date) / 1000)

    if (seconds < 60) return 'a few seconds ago'

    const minutes = Math.round(seconds / 60)
    if (minutes < 60)
        return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`

    const hours = Math.round(seconds / 3600)
    if (hours < 24) return hours === 1 ? 'an hour ago' : `${hours} hours ago`

    const days = Math.round(seconds / 86400)
    if (days < 30) return days === 1 ? 'a day ago' : `${days} days ago`

    const months = Math.round(seconds / 2592000)
    if (months < 12)
        return months === 1 ? 'a month ago' : `${months} months ago`

    const years = Math.round(seconds / 31536000)
    return years === 1 ? 'a year ago' : `${years} years ago`
}

/**
 * Checks if a string is in HH:mm or HH:mm:ss format.
 *
 * @param {string|number} timeValue - The value to check.
 * @returns {boolean} True if the value matches a time format, false otherwise.
 */
const isTimeFormat = (timeValue) => {
    return /^\d{2}:\d{2}(?::\d{2})?$/.test(String(timeValue))
}

/**
 * Converts a HH:mm or HH:mm:ss time value into a Date object.
 *
 * Uses today's date because JavaScript Date objects require a date component.
 *
 * @param {string|number} timeValue - Time string, for example "16:44" or "16:44:30".
 * @returns {Date|null} A valid Date object, or null if invalid.
 */
const getDateFromTimeValue = (timeValue) => {
    if (!isTimeFormat(timeValue)) return null

    const [hours, minutes, seconds = '00'] = String(timeValue).split(':')
    const date = new Date()

    date.setHours(Number(hours), Number(minutes), Number(seconds), 0)

    if (
        date.getHours() !== Number(hours) ||
        date.getMinutes() !== Number(minutes) ||
        date.getSeconds() !== Number(seconds)
    ) {
        return null
    }

    return date
}

export {
    isUnixTimestamp,
    formatDate,
    formatDateISO,
    isDate,
    getTimeFromDate,
    getDateFromNow,
    formatDateAndTime,
    convertDMYToISO,
    isDayMonthYearFormat,
    isDateTime,
    getDateFromDayMonthYearValue,
    parseDateValue,
    getDateFromValue,
    getDateFromTimeValue,
    isTimeFormat,
}
